<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import HighlightedText from '@/components/quran/HighlightedText.vue';
import UiSearchField from '@/components/ui/UiSearchField.vue';
import { useAsyncData } from '@/composables/useAsyncData';
import { searchAyahs } from '@/utils/quran/api';
import { ayahPath, searchPath, surahPath, wordsPath } from '@/utils/quran/paths';

/**
 * The Quran search field with live suggestions: a direct jump for "2:255",
 * surahs by name, the first ayat, and one-tap links to all results and to
 * counting the word. Enter opens the full results page.
 */
const props = defineProps({
  size: { type: String, default: 'lg' },
  tone: { type: String, default: 'plain' },
  initial: { type: String, default: '' },
  autofocus: { type: Boolean, default: false },
  // Live keyword suggestions; off when the search is by meaning.
  suggestions: { type: Boolean, default: true },
  // Extra query kept on the results page (e.g. { by: 'meaning' }).
  searchQuery: { type: Object, default: () => ({}) },
});

const { t, locale } = useI18n();
const router = useRouter();

const query = ref(props.initial);
const debounced = ref(props.initial);
const open = ref(false);
const highlighted = ref(-1);

let timer = null;
watch(query, (q) => {
  clearTimeout(timer);
  timer = setTimeout(() => (debounced.value = q.trim()), 250);
  open.value = true;
  highlighted.value = -1;
});

const { data, loading } = useAsyncData(
  () => (props.suggestions && debounced.value.length >= 2 && open.value ? { q: debounced.value, lang: locale.value } : null),
  (params) => searchAyahs(params),
  { keepPrevious: true }
);

/** Every row of the dropdown, in keyboard order. */
const items = computed(() => {
  const q = query.value.trim();
  if (q.length < 2 || !data.value) return [];
  const rows = [];
  const { jump, surahs, data: hits } = data.value;

  if (jump) rows.push({ kind: 'jump', to: ayahPath(jump.surah, jump.ayah), label: t('quran.search.jump', { ref: `${jump.surah}:${jump.ayah}` }) });
  for (const s of surahs) rows.push({ kind: 'surah', to: surahPath(s.id), surah: s });
  for (const hit of hits.slice(0, 4)) rows.push({ kind: 'ayah', to: ayahPath(hit.surah, hit.number), hit });
  rows.push({ kind: 'all', to: searchPath(q, props.searchQuery), label: t('quran.search.allResults', { q }) });
  if (/\p{L}/u.test(q)) rows.push({ kind: 'count', to: wordsPath(q), label: t('quran.search.countWord', { q }) });
  return rows;
});

const hitLang = computed(() => data.value?.meta.lang ?? locale.value);

function go(item) {
  open.value = false;
  router.push(item.to);
}

function submit() {
  const q = query.value.trim();
  if (!q) return;
  if (highlighted.value >= 0 && items.value[highlighted.value]) return go(items.value[highlighted.value]);
  const jump = data.value?.jump;
  if (jump && debounced.value === q) return go({ to: ayahPath(jump.surah, jump.ayah) });
  go({ to: searchPath(q, props.searchQuery) });
}

function onKeydown(event) {
  if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
    event.preventDefault();
    open.value = true;
    const n = items.value.length;
    if (!n) return;
    highlighted.value = (highlighted.value + (event.key === 'ArrowDown' ? 1 : -1) + n) % n;
  } else if (event.key === 'Escape') {
    open.value = false;
  }
}

function onBlur() {
  setTimeout(() => (open.value = false), 150);
}

const icons = {
  jump: 'solar:arrow-right-up-linear',
  surah: 'solar:book-2-linear',
  ayah: 'solar:document-text-linear',
  all: 'solar:magnifer-linear',
  count: 'solar:chart-square-linear',
};
</script>

<template>
  <div class="relative w-full">
    <UiSearchField
      v-model="query"
      :label="t('quran.search.label')"
      :placeholder="t('quran.search.placeholder')"
      :size="size"
      :tone="tone"
      :loading="loading"
      :autofocus="autofocus"
      @submit="submit"
      @keydown="onKeydown"
      @focus="open = true"
      @blur="onBlur"
    >
      <template #trailing>
        <button
          type="submit"
          class="shrink-0 h-[calc(100%-12px)] px-4 sm:px-5 rounded-[16px] bg-deen-ink text-white text-sm font-semibold hover:bg-deen-ink/90 transition-colors"
        >
          {{ t('quran.search.submit') }}
        </button>
      </template>
    </UiSearchField>

    <Transition
      enter-active-class="transition duration-150 ease-out"
      enter-from-class="opacity-0 -translate-y-1"
      leave-active-class="transition duration-100 ease-in"
      leave-to-class="opacity-0"
    >
      <ul
        v-if="open && items.length"
        role="listbox"
        :aria-label="t('quran.search.suggestions')"
        class="absolute z-40 left-0 right-0 mt-2 max-h-[70vh] overflow-y-auto rounded-3xl bg-white border border-deen-ink/10 shadow-[0_30px_80px_-20px_rgba(28,72,72,0.4)] p-2 text-left"
      >
        <li v-for="(item, index) in items" :key="index" role="option" :aria-selected="highlighted === index">
          <button
            type="button"
            :class="[
              'w-full flex items-start gap-3 px-3 py-2.5 rounded-2xl text-left transition-colors',
              highlighted === index ? 'bg-deen-sand/70' : 'hover:bg-deen-mist',
            ]"
            @mousedown.prevent="go(item)"
          >
            <iconify-icon :icon="icons[item.kind]" class="mt-0.5 text-lg text-deen-ink/40 shrink-0" aria-hidden="true" />
            <span v-if="item.kind === 'surah'" class="flex-1 min-w-0 flex items-center justify-between gap-3">
              <span class="min-w-0">
                <span class="block text-sm font-semibold text-deen-ink truncate">{{ item.surah.id }}. {{ item.surah.transliteration }}</span>
                <span class="block text-xs text-deen-ink/50 truncate">{{ item.surah.translation[locale] ?? item.surah.translation.en }}</span>
              </span>
              <span dir="rtl" lang="ar" class="arabic-inline text-xl text-deen-ink/70">{{ item.surah.name_arabic }}</span>
            </span>
            <span v-else-if="item.kind === 'ayah'" class="flex-1 min-w-0">
              <span class="block text-xs font-semibold text-deen-ink/50 tabular-nums mb-0.5">{{ item.hit.key }}</span>
              <span v-if="hitLang === 'ar'" dir="rtl" lang="ar" class="block font-quran text-lg text-deen-ink line-clamp-2 leading-loose">{{ item.hit.arabic }}</span>
              <span v-else class="block text-sm text-deen-ink/80 line-clamp-2">
                <HighlightedText :text="item.hit.translation[hitLang]" :ranges="item.hit.marks[hitLang] ?? []" />
              </span>
            </span>
            <span v-else class="flex-1 text-sm font-semibold text-deen-ink">{{ item.label }}</span>
          </button>
        </li>
      </ul>
    </Transition>
  </div>
</template>
